/**
 * Cursive alphabets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cursiveAlphabetsPack: ToilePack = {
    slug: 'cursive-alphabets',
    category: 'illustration',
    name: 'Cursive alphabets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'school', label: 'School cursive', subject: 'a classroom chart of cursive capitals and lowercase pairs "Aa" to "Zz" on ruled handwriting lines with a dashed midline, small arrows showing the stroke direction'},
        {key: 'lowercase', label: 'Lowercase joined', subject: 'a chart of the lowercase cursive "a" to "z" on ruled lines, each letter with its entry and exit stroke so the joins are clear'},
        {key: 'elegant', label: 'Elegant script', subject: 'a chart of an elegant cursive alphabet, capitals "A" to "Z" with looped entry strokes and generous swashes, in six rows on a clean page'},
    ],
    styles: [
        {key: 'upright', label: 'Upright school', style: 'upright school cursive with a round, even stroke and the joins plainly visible', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'slanted', label: 'Slanted', style: 'slanted cursive with a light, quick stroke and narrow oval letters', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'guided', label: 'With guides', style: 'school cursive on ruled lines with a dashed midline and a small numbered arrow at the start of each stroke', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['capitals only', 'a name', 'numbers'],
    directionHint: 'try: capitals only',
    seo: {
        title: 'Cursive Alphabets: School Charts and Elegant Script',
        metaDescription: 'Nine cursive alphabet sheets: school cursive on ruled lines with stroke arrows, and an elegant script alphabet for cards.',
        h1: 'Cursive alphabets: school cursive charts and an elegant script',
        tagline: 'School capitals and lowercase with arrows, plus an elegant alphabet for cards and invitations.',
        intro: 'A cursive alphabet chart helps a child learn joined-up writing and helps an adult relearn it. The nine sheets show school cursive capitals and lowercase pairs on ruled lines with stroke arrows, the lowercase letters with each entry and exit stroke, and an elegant script alphabet with looped capitals. Each school sheet comes upright, slanted or on guided lines. Ask for capitals only, a name or numbers, and the chart is set that way.',
        useCases: [
            'a handwriting practice book for a child',
            'a reference for an adult relearning joined writing',
            'place cards or envelope lettering',
            'a wedding invitation',
        ],
        faq: [
            {q: 'What does the school cursive look like?', a: 'A plain, even cursive on ruled lines with a dashed midline, so the joins are easy to see.'},
            {q: 'What is the difference between upright and slanted?', a: 'Upright has a round, even stroke with the joins plainly visible. Slanted has a lighter, quicker stroke and narrow oval letters.'},
            {q: 'What do the arrows show?', a: 'The guided sheet has a small numbered arrow at the start of each stroke, showing the direction to write it.'},
            {q: 'Can I get my name in cursive?', a: 'Ask for a name, capitals only or numbers, and the chart is set to that subject.'},
        ],
    },
}
